import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import HomeIcon from "@mui/icons-material/HomeOutlined";
import ReceiptIcon from "@mui/icons-material/ReceiptLongOutlined";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import { Box, MenuList } from "@mui/material";
import { useMatch } from "react-router";
import useClientContext from "../../context/useClientContext";
import useInvestorRelationsAccess from "../../hooks/useInvestorRelationsAccess";
import InvestorRelationsIcon from "@entrilia/shared/icons/CardAccountDetailsIcon";
import DashboardIcon from "@entrilia/shared/icons/DashboardIcon";
import FilesIcon from "@entrilia/shared/icons/FilesIcon";
import PortfolioMonitoringIcon from "../common/domain-icons/PortfolioMonitoringIcon";
import ReportingProductIcon from "../common/domain-icons/ReportingProductIcon";
import SparklesIcon from "@entrilia/shared/icons/SparklesIcon";
import WorkflowsIcon from "@entrilia/shared/icons/WorkflowsIcon";
import { pageRoutes } from "../../routes";
import BetaLabel from "../common/BetaLabel";
import MenuGroupItem from "../common/MenuGroupItem";
import MenuLinkItem from "../common/MenuLinkItem";
import CompanySmartViewsMenu from "./CompanySmartViewsMenu";
import CorpAccountingMenuLinkItem from "./CorpAccountingMenuLinkItem";
import ExpenseManagementMenu from "./ExpenseManagementMenu";
import FundAdminWorkflowsMenu from "./FundAdminWorkflowsMenu";
import FundOpsMenuLinkItem from "./FundOpsMenuLinkItem";
import InvestorRelationsMenu from "./InvestorRelationsMenu";
import OrganizationSmartViewsMenu from "./OrganizationSmartViewsMenu";
import PortfolioMenu from "./PortfolioMenu";
import SettingsMenu from "./SettingsMenu";
import WorkflowsMenu from "./WorkflowsMenu";

const MainMenu = () => {
  const routeMatch = useMatch("/:client/:pageName/*");
  const currentPageName = routeMatch?.params.pageName;

  const {
    clientCode,
    isFundAdmin,
    businessCentralApp,
    investorRelationsApp,
    portfolioMonitoringApp,
    reportingPortalApp,
    expenseManagementPortalApp,
    workflowsApp,
    hasAnyPermission,
    hasPermissions,
    isAllowedToAccessAllEntities,
    isPortalFeatureEnabled,
    menuBadges,
  } = useClientContext();

  const { hasAccessToInvestorRelations } = useInvestorRelationsAccess();

  const hasAccessToViewFundOps = hasAnyPermission(["ViewFundOpsLink"]);

  const hasAccessToViewCorpAccounting = hasAnyPermission(["ViewCorpAccountingLink"]);

  const hasAccessToReportingPortal = hasAnyPermission(["ManageReports", "ViewReports"]);

  const hasAccessToOrganizationDataModel = hasAnyPermission([
    "ViewOrganizationDataModel",
    "ManageOrganizationDataModel",
  ]);

  const hasAccessToOrganizationUsers = hasAnyPermission(["ManageOrganizationUsers", "ViewOrganizationUsers"]);

  const hasAccessToCompanySettings = hasAnyPermission(["ViewCompanySettings", "ManageCompanySettings"]);

  const hasAccessToIntegrationSettings = hasAnyPermission(["ViewClientIntegrations", "ManageClientIntegrations"]);

  const hasAccessToImportData =
    isAllowedToAccessAllEntities &&
    hasAnyPermission([
      "ViewInvestorRelationsDataImports",
      "ManageInvestorRelationsDataImports",
      "ViewPortfolioMonitoringDataImports",
      "ManagePortfolioMonitoringDataImports",
      "ViewExpenseManagementDataImports",
      "ManageExpenseManagementDataImports",
    ]);

  const hasAccessToActivityLog = hasAnyPermission(["ViewActivityLog"]);

  const hasAccessToAssistant = hasAnyPermission(["UseAiAssistant"]);

  const hasAccessToOrganizationSettings = hasAccessToOrganizationUsers || hasAccessToOrganizationDataModel;

  const hasAccessToClientSettings =
    hasAccessToCompanySettings ||
    hasAccessToIntegrationSettings ||
    (hasAccessToImportData && !!reportingPortalApp) ||
    hasAccessToActivityLog;

  const hasAccessToCompanyFiles = hasAnyPermission(["ViewAllCompanyFiles", "ManageAllCompanyFiles"]);

  const hasAccessToOrganizationReports = hasAnyPermission([
    "ManageOrganizationReports",
    "ManageOrganizationReportTemplates",
  ]);

  const hasAccessToExpenseManagement = hasAnyPermission(["ViewExpenses", "ManageExpenses"]);

  const hasAccessToPortfolioMonitoringManagement = hasAnyPermission([
    "ViewPortfolioMonitoring",
    "ManagePortfolioMonitoring",
  ]);

  const hasAccessToAssignedDataSubmission = hasPermissions(["ManagePortfolioMonitoringAssignedDataSubmissions"]);

  const isDataCollectionEnabled = !!portfolioMonitoringApp?.dataCollection?.enabled;

  const hasAccessToPortfolioMonitoring =
    hasAccessToPortfolioMonitoringManagement || (isDataCollectionEnabled && hasAccessToAssignedDataSubmission);

  const hasAccessToDashboards = hasAnyPermission(["ViewDashboards", "ManageDashboards"]);
  const hasAccessToAiDashboards =
    isPortalFeatureEnabled("AiDashboardsEnabled") &&
    hasAccessToDashboards &&
    hasAnyPermission(["ViewReports", "ManageReports"]) &&
    !isFundAdmin &&
    !!reportingPortalApp;

  const hasAccessToWorkflows = hasAnyPermission(["ViewWorkflows", "ManageWorkflows"]);

  return (
    <MenuList>
      {hasAccessToAssistant && isPortalFeatureEnabled("AiAssistantEnabled") && (
        <Box sx={[{ mb: 1 }, (t) => ({ border: "1px solid " + t.palette.divider, borderRadius: 0.5 })]}>
          <MenuLinkItem
            active={currentPageName === pageRoutes.assistant}
            title="Your Assistant"
            href={`/${clientCode}/${pageRoutes.assistant}`}
            Icon={SparklesIcon}
            badgeCount={menuBadges?.unreadAssistantChatsCount}
          />
        </Box>
      )}
      {!isFundAdmin && (
        <MenuLinkItem
          active={currentPageName === pageRoutes.home}
          title="Home"
          href={`/${clientCode}/${pageRoutes.home}`}
          Icon={HomeIcon}
        />
      )}
      {!isFundAdmin && businessCentralApp?.fundAccountingEnabled && hasAccessToViewFundOps && <FundOpsMenuLinkItem />}
      {!isFundAdmin && businessCentralApp?.corporateAccountingEnabled && hasAccessToViewCorpAccounting && (
        <CorpAccountingMenuLinkItem />
      )}
      {!isFundAdmin && hasAccessToCompanyFiles && isPortalFeatureEnabled("EntityFilesEnabled") && (
        <MenuLinkItem
          active={currentPageName === pageRoutes.files}
          title="Files"
          href={`/${clientCode}/${pageRoutes.files}`}
          Icon={FilesIcon}
        />
      )}
      {hasAccessToAiDashboards && (
        <MenuLinkItem
          active={currentPageName === pageRoutes.aiDashboards}
          title="AI Dashboards"
          href={`/${clientCode}/${pageRoutes.aiDashboards}`}
          Icon={DashboardCustomizeOutlinedIcon}
          chip={<BetaLabel />}
        />
      )}
      {hasAccessToDashboards && (
        <MenuLinkItem
          active={currentPageName === pageRoutes.dashboards}
          title="Dashboards"
          href={`/${clientCode}/${pageRoutes.dashboards}`}
          Icon={DashboardIcon}
        />
      )}
      <Box sx={{ width: "100%", height: "1.25rem" }} />
      {!isFundAdmin && reportingPortalApp && hasAccessToReportingPortal && (
        <MenuGroupItem title="SmartViews" Icon={ReportingProductIcon}>
          <CompanySmartViewsMenu />
        </MenuGroupItem>
      )}
      {isFundAdmin && hasAccessToOrganizationReports && (
        <MenuGroupItem title="SmartViews" Icon={ReportingProductIcon}>
          <OrganizationSmartViewsMenu />
        </MenuGroupItem>
      )}
      {!isFundAdmin && investorRelationsApp && hasAccessToInvestorRelations && (
        <MenuGroupItem title="Investor Relations" Icon={InvestorRelationsIcon}>
          <InvestorRelationsMenu />
        </MenuGroupItem>
      )}
      {!isFundAdmin && (expenseManagementPortalApp || (workflowsApp && hasAccessToWorkflows)) && (
        <MenuGroupItem id="main-menu-workflows" title="Workflows" Icon={WorkflowsIcon}>
          <WorkflowsMenu />
        </MenuGroupItem>
      )}
      {isFundAdmin && hasAccessToOrganizationDataModel && isPortalFeatureEnabled("ObjectChecklistsEnabled") && (
        <MenuGroupItem title="Workflows" Icon={WorkflowsIcon}>
          <FundAdminWorkflowsMenu />
        </MenuGroupItem>
      )}
      {!isFundAdmin && portfolioMonitoringApp && hasAccessToPortfolioMonitoring && (
        <MenuGroupItem title="Portfolio" Icon={PortfolioMonitoringIcon}>
          <PortfolioMenu />
        </MenuGroupItem>
      )}
      {!isFundAdmin && expenseManagementPortalApp && hasAccessToExpenseManagement && (
        <MenuGroupItem title="Expense Management" Icon={ReceiptIcon}>
          <ExpenseManagementMenu />
        </MenuGroupItem>
      )}
      {((isFundAdmin && hasAccessToOrganizationSettings) || (!isFundAdmin && hasAccessToClientSettings)) && (
        <MenuGroupItem title="Settings" Icon={SettingsIcon}>
          <SettingsMenu />
        </MenuGroupItem>
      )}
    </MenuList>
  );
};

export default MainMenu;
